import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';

interface TableComponentProps<T> {
    headers: string[];
    data: T[];
    error?: string | null;
    loading?: boolean;
    renderRow: (item: T, index: number) => JSX.Element;
}

const TableComponent = <T,>({
    headers,
    data,
    renderRow,
}: TableComponentProps<T>) => (
    <div className="bg-white rounded-[2rem] border border-slate-100/80 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.03)] overflow-hidden mt-6 transition-all duration-300 hover:shadow-[0_16px_48px_-10px_rgba(0,0,0,0.06)]">
        <TableContainer
            sx={{
                maxHeight: '520px',
                overflowY: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                '& .MuiTable-root': {
                    borderCollapse: 'collapse',
                },
                '& .MuiTableCell-root': {
                    fontFamily: '"Jost", "Inter", sans-serif',
                    transition: 'all 0.15s ease-in-out',
                },
                '& .MuiTableCell-head': {
                    backgroundColor: '#f8fafc',
                    color: '#475569',
                    fontSize: '0.675rem',
                    fontWeight: '800',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    borderBottom: '1px solid #f1f5f9',
                    padding: '18px 24px',
                },
                '& .MuiTableRow-root': {
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: '#f8fafc !important',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px -2px rgba(0,0,0,0.02)',
                    },
                },
                '& .MuiTableCell-body': {
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#334155',
                    borderBottom: '1px solid #f8fafc',
                    padding: '16px 24px',
                },
            }}
        >
            <Table stickyHeader className="min-w-full">
                <TableHead>
                    <TableRow>
                        {headers.map((header) => (
                            <TableCell key={header}>
                                {header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.length > 0 ? (
                        data.map((item, index) => renderRow(item, index))
                    ) : (
                        <TableRow>
                            <TableCell
                                colSpan={headers.length}
                                sx={{
                                    textAlign: 'center',
                                    padding: '64px 24px !important',
                                    color: '#94a3b8',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    fontStyle: 'italic',
                                }}
                            >
                                <div className="flex flex-col items-center justify-center gap-3">
                                    <svg className="w-12 h-12 text-slate-200 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0a2 2 0 01-2 2H6a2 2 0 01-2-2m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414a1 1 0 00-.707-.293H4" />
                                    </svg>
                                    <span>No records available at the moment.</span>
                                </div>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    </div>
);

export default TableComponent;
